import { Offcanvas } from 'react-bootstrap';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import { useObjectCard } from '../context/ObjectCardProvider';
import HistoryPriceForm from './HistoryPriceForm';
import {
    getActualObjectCost,
    submitHistoryPrice,
    useHistoryPrice,
    type HistoryPriceFormValues,
} from './model';

interface OffHistoryPriceProps {
    show: boolean;
    setShow: (show: boolean) => void;
    objectId: number;
    objectPrice: number;
    objectPriceRate: number;
    onSuccess: (objectPrice: number, objectPriceRate: number) => Promise<void>;
}

const OffHistoryPrice = ({
    show,
    setShow,
    objectId,
    objectPrice,
    objectPriceRate,
    onSuccess,
}: OffHistoryPriceProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const { vObject, vObjectInfo } = useObjectCard();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const { history, loading, reload } = useHistoryPrice(objectId, show);
    const actualCost = useMemo(
        () => getActualObjectCost(vObject, vObjectInfo),
        [vObject, vObjectInfo],
    );

    const handleClose = () => setShow(false);

    const initialValues: HistoryPriceFormValues = {
        objectPrice: Number(objectPrice) || 0,
        objectPriceRate: Number(objectPriceRate) || 0,
        comment: '',
    };

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="off-history-price">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('historyPrice.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={(values, helpers) =>
                        submitHistoryPrice(values, helpers, {
                            objectId,
                            t,
                            showToast,
                            onSuccess: async (nextPrice, nextRate) => {
                                await onSuccess(nextPrice, nextRate);
                                helpers.setFieldValue('comment', '');
                                helpers.setFieldValue('objectPrice', nextPrice);
                                helpers.setFieldValue('objectPriceRate', nextRate);
                                await reload();
                            },
                        })
                    }
                >
                    {({
                        handleSubmit,
                        isSubmitting,
                        values,
                        setFieldValue,
                        errors,
                        touched,
                    }) => (
                        <HistoryPriceForm
                            values={values}
                            setFieldValue={setFieldValue}
                            handleSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            priceError={
                                touched.objectPrice && errors.objectPrice
                                    ? String(errors.objectPrice)
                                    : ''
                            }
                            history={history}
                            historyLoading={loading}
                            isUahMode={isUahMode}
                            rate={rate}
                            actualCost={actualCost}
                            autofocus={show}
                        />
                    )}
                </Formik>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffHistoryPrice;
